import { AdminBoardConfig, AvailableSection } from '../api/asanaBoard';

export type AdminFieldMode = 'hidden' | 'read' | 'edit';

// A field cannot group the Board once it's hidden from the client — the
// column headers would carry values nobody flagged a field to show. Two
// places in AdminModuleAsanaBoard.tsx can set a field's mode (the chosen-
// fields table, and the "Available on this project" list), and both must
// drop boardGroupFieldGid when the field they're hiding is the one currently
// selected for grouping. Routing both call sites through here is what keeps
// that guard from drifting apart as the two get edited independently.
//
// `defaultLabel` is used only the first time a field is set to read/edit —
// it seeds the client-facing column label (from the field's Asana name) when
// there's no existing row to carry one forward.
export function applyFieldMode(
  cfg: AdminBoardConfig,
  fieldGid: string,
  mode: AdminFieldMode,
  defaultLabel?: string,
): AdminBoardConfig {
  const boardGroupFieldGid =
    mode === 'hidden' && cfg.boardGroupFieldGid === fieldGid ? null : cfg.boardGroupFieldGid;

  if (mode === 'hidden') {
    const fields = cfg.fields
      .filter((f) => f.fieldGid !== fieldGid)
      .map((f, i) => ({ ...f, sortOrder: i }));
    return { ...cfg, fields, boardGroupFieldGid };
  }

  const existing = cfg.fields.find((f) => f.fieldGid === fieldGid);
  const row = {
    fieldGid,
    label: existing?.label || defaultLabel || fieldGid,
    mode,
    sortOrder: existing?.sortOrder ?? cfg.fields.length,
  };
  const fields = (existing
    ? cfg.fields.map((f) => (f.fieldGid === fieldGid ? row : f))
    : [...cfg.fields, row]
  ).map((f, i) => ({ ...f, sortOrder: i }));

  return { ...cfg, fields, boardGroupFieldGid };
}

// Where the chosen intake section sits on the board. Intake belongs after the
// delivery sections, so the List view shows the work first and the triage
// queue last; the picker says so when the choice is anything else.
export type IntakePlacement =
  | { kind: 'none' }
  // A GID is set but is not one of the project's sections: pasted by hand, or
  // left over from a previous project. The server refuses it on save.
  | { kind: 'unlisted' }
  | { kind: 'last'; position: number; total: number }
  | { kind: 'not-last'; position: number; total: number };

export function intakePlacement(
  sections: AvailableSection[] | null,
  intakeSectionGid: string | null,
): IntakePlacement {
  if (!intakeSectionGid) return { kind: 'none' };
  const hit = sections?.find((s) => s.gid === intakeSectionGid);
  if (!hit || !sections) return { kind: 'unlisted' };
  return { kind: hit.isLast ? 'last' : 'not-last', position: hit.position, total: sections.length };
}
